/**
 * The Memories screen and the Home card that points at it.
 *
 * Written for a customer, not an operator: nothing here names a triple, a tier,
 * a confidence or who taught a memory. What a person needs to know is the
 * sentence, whether their Smith is using it yet, and — for their own — what
 * they can do about it.
 */

export const memories = {
  title: 'Memories',
  subtitle: "What your Smiths remember, so you don't have to say it twice.",
  loading: 'Loading what your Smiths remember…',
  empty: 'Nothing has been remembered yet. As you work, your Smiths pick up how you like things done.',
  noSmithKeeping: 'None of your Smiths is keeping memories yet.',
  emptyHere: "This Smith isn't one you can see here.",
  showAll: 'See them all',
  filterBySmith: 'Show memories from',
  allSmiths: 'All Smiths',

  // Sections
  yours: 'Yours',
  yoursSub: 'Only you see these, and only you decide about them.',
  company: 'Your company',
  companySub: (name: string) => `Everyone at ${name} works with these. Your administrator manages them.`,
  unavailable: 'These could not be loaded right now. Try again shortly.',
  // The listing was cut at its ceiling: said, so a short list is not read as the whole of it.
  listCut: 'This list shows only the most recent memories of this Smith.',
  // Two different things happen at the cap, and the notice says both: what the Smith learns waits, and what a person
  // writes or takes is refused until room is made.
  capacityNear:
    "This Smith's memory is almost full. Once it is, what the Smith learns waits before it is used, and a memory can be added or put to use only after another stops being used.",
  capacityFull:
    "This Smith's memory is full. What the Smith learns now waits before it is used, and a memory can be added or put to use only after another stops being used.",
  noneForSmith: 'Nothing remembered for this one yet.',

  // Status
  waiting: 'Waiting for you',
  inUse: 'In use',
  waitingCount: (n: number) => (n === 1 ? '1 waiting for you' : `${n} waiting for you`),
  savedOn: (when: string) => `Noted ${when}`,

  // What kind of thing it is
  kinds: {
    preference: 'Preference',
    correction: 'Correction',
    decision: 'Decision',
    procedure: 'Procedure',
    fact: 'Fact',
    negative: 'Restriction'
  } as Record<string, string>,

  // Acts on your own
  use: 'Use it',
  useHint: 'Your Smiths start applying it from the next run.',
  discard: 'Discard',
  discardHint: "Your Smiths won't apply it. A later run may still suggest the same thing again.",
  discardReasonLabel: "Why shouldn't this be used?",
  discardReasonPlaceholder: 'It was only true for that one request',
  discardReasonRequired: 'Please say why, in a few words.',
  stopUsing: 'Stop using',
  stopUsingHint: 'Your Smiths stop applying it. This cannot be undone.',
  reword: 'Reword',
  rewordHint: 'Saving your wording also puts it to use.',
  cancel: 'Cancel',
  save: 'Save',
  confirm: 'Confirm',
  working: 'Saving…',

  // The memory in one sentence, and its optional details. The numbers come from the service with the memories. The
  // sentence is the whole memory; the details never finish it, since the Smith reads the sentence first.
  titleLabel: 'Your memory, in one sentence',
  titleHint: 'Required. Say the whole thing in one complete sentence: it is what your Smith reads first.',
  detailsLabel: 'Details (optional)',
  detailsHint: (inline: number) =>
    `Context, examples or the reason behind it, not the rest of the sentence above. Leave it empty when the sentence says it all. Your Smith reads details of up to ${inline.toLocaleString('en-US')} characters together with the sentence, and longer ones when it needs them.`,
  count: (n: number, max: number) => `${n.toLocaleString('en-US')} / ${max.toLocaleString('en-US')} characters`,
  tooLong: (max: number) =>
    `The details can be ${max.toLocaleString('en-US')} characters at most. Shorten them to save.`,
  titleTooLong: (max: number) =>
    `The sentence can be ${max.toLocaleString('en-US')} characters at most. Say it more briefly; context and examples go in the details.`,
  needsTitle: 'Write your memory as one sentence in the first field. The details are optional.',
  showWhole: (n: number) => `Show all ${n.toLocaleString('en-US')} characters`,
  showLess: 'Show less',

  // Writing one yourself
  write: 'Teach it something',
  writePlaceholder: 'Write it the way you would say it',
  writeHint: 'Only you see it, and it is in use from your next run. You can reword or remove it later.',
  writeSave: 'Save it',
  writeKindLabel: 'What kind of thing is it?',
  /**
   * A label and a real example per kind. The example is the whole point: "a
   * preference" and "a restriction" are abstractions, and the difference between
   * them decides whether the Smith treats the sentence as something to aim for
   * or something never to do.
   */
  writeKinds: {
    preference: {
      label: 'Something I prefer',
      example: 'Send me a short summary of what changed before the full document.'
    },
    prohibition: {
      label: 'Something that should never happen',
      example: 'Never quote a price in dollars in anything you send me.'
    },
    fact: {
      label: 'Something it should know',
      example: 'I look after the purchasing area, so requests from me are about purchasing.'
    }
  } as Record<string, { label: string; example: string }>,
  writeExample: (example: string) => `For example: “${example}”`,

  // Home card
  homeTitle: (n: number) => (n === 1 ? 'A memory is waiting for you' : `${n} memories are waiting for you`),
  homeSubWithSmith: (name: string) => `${name} noted something about how you like to work.`,
  homeSub: 'Something was noted about how you like to work.',
  homeCta: 'Review'
}
